import React, { useState, useEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { Loader } from "lucide-react";
import Context from "../../../../Context/Context";
import { getCountryCodeFromName } from "../../../../Utils/country-mapping";

// Custom Dropdown Component
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  disabled = false,
  required = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        portalRef.current && !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const clickableOptions = options.filter(opt => !opt.disabled);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !selectedOption ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : ''
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <FaChevronDown 
          className={`ml-2 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={portalRef}
          className={`fixed z-[99999] rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {clickableOptions.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No options available
              </div>
            ) : (
              clickableOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange({ target: { value: option.value } });
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors duration-150 ${
                    value === option.value
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                      : isDarkMode
                      ? 'text-white hover:bg-[#2a2a2a]'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const MemberModal = ({
  isOpen,
  onClose,
  memberData,
  isDarkMode = true,
  onSave,
  readOnly = false,
}) => {
  const Ctx = useContext(Context);
  const UtilCtx = useContext(Context).util;
  const [loading, setLoading] = useState(false);
  const isEditMode = !!memberData;

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [status, setStatus] = useState("Active");
  const [balance, setBalance] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Load member data when modal opens
  useEffect(() => {
    if (memberData && isOpen) {
      setName(memberData.userName || "");
      setEmail(memberData.emailId || "");
      setPhoneNumber(memberData.phoneNumber || "");
      setStatus(memberData.status || "Active");
      setBalance(memberData.balance || "");
      setProfileImagePreview(memberData.imgUrl || null);
      setProfileImage(null);
    } else {
      resetForm();
    }
  }, [memberData, isOpen]);

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
    setStatus("Active");
    setBalance("");
    setProfileImage(null);
    setProfileImagePreview(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !phoneNumber) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const formData = {
        userName: name,
        emailId: email,
        phoneNumber,
        status,
        balance,
      };

      // If there's a new image file, add it to formData
      if (profileImage) {
        formData.profileImage = profileImage;
      }

      if (onSave) {
        await onSave(formData);
      }
      handleClose();
    } catch (error) {
      toast.error(error.message || "Error saving member");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center ${isDarkMode ? 'bg-black/60' : 'bg-black/20'} backdrop-blur-md transition-opacity duration-300 p-2 md:p-4`}
        onClick={handleClose}
      >
        <div 
          className={`${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-white'} rounded-xl md:rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] md:max-h-[90vh] flex flex-col transition-all duration-300 transform scale-100`}
          style={{ fontFamily: "Inter, sans-serif" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`px-3 md:px-6 py-2.5 md:py-3 flex-shrink-0`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col items-start gap-1.5 md:gap-3 flex-1">
                {!readOnly && !isEditMode && (
                  <span className="bg-[#FDCF08] text-black text-[10px] md:text-xs font-[600] tracking-wider px-2 md:px-3 py-0.5 md:py-1 rounded-full uppercase">
                    NEW MEMBER
                  </span>
                )}
                {!readOnly && isEditMode && (
                  <span className="bg-[#FDCF08] text-black text-[10px] md:text-xs font-[600] tracking-wider px-2 md:px-3 py-0.5 md:py-1 rounded-full uppercase">
                    EDIT MEMBER
                  </span>
                )}
                
                <h2 className={`text-base md:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {readOnly ? "View Member" : isEditMode ? "Edit Member" : "Create Member"}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className={`w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center transition-all duration-200 flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'} focus:outline-none`}
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {!readOnly && !isEditMode && (
              <p className={`text-[11px] md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1 md:mt-2`}>
                Add a new member to your system with their contact details and status.
              </p>
            )}
            {!readOnly && isEditMode && (
              <p className={`text-[11px] md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1 md:mt-2`}>
                Update member information including name, email, phone and status.
              </p>
            )}
          </div>

          {/* Scrollable Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-3 md:px-6 pb-3 md:pb-4 space-y-3 md:space-y-5">
            {/* Profile Image Upload */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Profile Image
              </label>
              <div className="flex items-center gap-3 md:gap-4">
                {/* Image Preview */}
                <div className={`w-16 h-16 md:w-20 md:h-20 rounded-lg flex-shrink-0 overflow-hidden flex items-center justify-center border-2 ${
                  isDarkMode 
                    ? 'bg-[#1a1a1a] border-[#2a2a2a]' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  {profileImagePreview ? (
                    <img 
                      src={profileImagePreview} 
                      alt="Profile preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg 
                      className={`w-8 h-8 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M12 4v16m8-8H4" 
                      />
                    </svg>
                  )}
                </div>

                {/* Upload Buttons */}
                <div className="flex-1 flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={readOnly}
                    className="hidden"
                    id="profile-image-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={readOnly}
                    className={`w-full h-8 md:h-10 px-3 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${
                      readOnly
                        ? 'opacity-50 cursor-not-allowed'
                        : isDarkMode
                        ? 'bg-[#FDCF08]/20 text-[#FDCF08] hover:bg-[#FDCF08]/30'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] hover:bg-[#FDCF08]/20'
                    }`}
                  >
                    Upload Image
                  </button>
                  {profileImagePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      disabled={readOnly}
                      className={`w-full h-8 md:h-10 px-3 text-xs md:text-sm font-medium rounded-lg transition-all duration-200 ${
                        readOnly
                          ? 'opacity-50 cursor-not-allowed'
                          : isDarkMode
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Member Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter member name"
                disabled={readOnly}
                className={`border-none w-full h-9 md:h-11 pl-3 md:pl-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' : 'bg-white border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'}`}
                required
              />
            </div>

            {/* Email */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={readOnly}
                className={`border-none w-full h-9 md:h-11 pl-3 md:pl-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' : 'bg-white border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'}`}
                required
              />
            </div>

            {/* Phone */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Phone Number *
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter phone number"
                disabled={readOnly}
                className={`border-none w-full h-9 md:h-11 pl-3 md:pl-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' : 'bg-white border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'}`}
                required
              />
            </div>

            {/* Status and Balance Row */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {/* Status */}
              <div className="w-full space-y-1.5 md:space-y-2">
                <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  Status
                </label>
                <CustomDropdown
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "InActive", label: "Inactive" }
                  ]}
                  placeholder="Select status"
                  isDarkMode={isDarkMode}
                  disabled={readOnly}
                />
              </div>

              {/* Balance */}
              <div className="w-full space-y-1.5 md:space-y-2">
                <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  Balance
                </label>
                <input
                  type="number"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  placeholder="Enter balance"
                  disabled={readOnly}
                  className={`border-none w-full h-9 md:h-11 pl-3 md:pl-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' : 'bg-white border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'}`}
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          {!readOnly && (
            <div className={`px-3 md:px-6 py-2.5 md:py-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 border-t flex-shrink-0 ${isDarkMode ? 'border-[#1a1a1a]' : 'border-gray-100'}`}>
              <button
                type="button"
                onClick={handleClose}
                className={`h-9 md:h-11 px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all duration-200 ${isDarkMode ? 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="h-9 md:h-11 px-3 bg-[#FDCF08] text-black font-semibold rounded-lg flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm hover:bg-[#f0c000] focus:outline-none focus:ring-2 focus:ring-[#FDCF08] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {isEditMode ? "Saving..." : "Creating..."}
                  </>
                ) : (
                  <>
                    {isEditMode ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Save Changes
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Create Member
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MemberModal;
